import { lazy, memo, Suspense, useCallback } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { ErrorBoundary } from '@cbhq/identity-shared';
import type {
  TwoFactorBoundaryError,
  TwoFactorConstraints,
  VerifyTwoFactorProps,
} from '@cbhq/identity-two-factor/types';
import { TwoFactorConstraintsMode, TwoFactorType } from '@cbhq/identity-two-factor/types';

import {
  oauthClientId,
  projectId,
  shouldMockdata,
  targetEnvironment,
} from '../configs/environments';
import { logError } from '../utils/logging';

import { InAppErrorMessageFallback } from './ErrorBoundary/InAppErrorMessageFallback';

const messages = defineMessages({
  loadErrorTitle: {
    id: 'StepUpTwoFactor.loadErrorTitle',
    defaultMessage: 'Verification unavailable',
    description: 'Title shown when the two-factor verification step fails to load',
  },
  loadErrorDescription: {
    id: 'StepUpTwoFactor.loadErrorDescription',
    defaultMessage: 'Refresh the page and try again.',
    description: 'Guidance shown when the two-factor verification step fails to load',
  },
});

const VerifyTwoFactor = lazy(async () =>
  import('@cbhq/identity-two-factor').then((mod) => ({ default: mod.VerifyTwoFactor })),
);

/**
 * Action recorded on the Janus challenge for the privileged member-management
 * mutations. Janus applies a 5-minute recent-validation bypass per action, so
 * stepping up for one member change covers the next few.
 */
export const WORKSPACE_MEMBER_MANAGEMENT_ACTION = 'web-Workspace-MemberManagement';

const ACCEPTED_TWO_FACTOR_TYPES: TwoFactorType[] = [
  TwoFactorType.SMS,
  TwoFactorType.TOTP,
  TwoFactorType.U2F,
  TwoFactorType.Passkey,
  TwoFactorType.Push,
];

// Every accepted type requires possession of a device at challenge time.
// RecoveryCode is excluded on those grounds: it is a static stored secret, so a
// dumped code would authorize a member change with nothing the user holds.
// NO_2FA/BYPASS would defeat the control outright and GUARDIAN is the separate
// consensus-approver path, so both are excluded along with every other type
// Janus can offer.
const STEP_UP_CONSTRAINTS: TwoFactorConstraints = {
  mode: TwoFactorConstraintsMode.Allow,
  types: ACCEPTED_TWO_FACTOR_TYPES,
};

export type StepUpTwoFactorProps = {
  onSuccess: (proofToken: string, twoFactorType: TwoFactorType) => void;

  onCancel: () => void;

  /**
   * Action recorded on the Janus challenge.
   * @default WORKSPACE_MEMBER_MANAGEMENT_ACTION
   */
  action?: string;

  /**
   * Called for errors surfaced by VerifyTwoFactor. A failed 2FA attempt is not
   * routed here: VerifyTwoFactor shows the error inline and lets the user retry
   * within the same challenge session.
   */
  onError?: VerifyTwoFactorProps['onError'];

  onToast?: VerifyTwoFactorProps['onToast'];

  /** Rendered while VerifyTwoFactor is being code-split in and while it loads. */
  loader?: ReactNode;

  rootBoxProps?: VerifyTwoFactorProps['rootBoxProps'];

  /**
   * Which Janus environment VerifyTwoFactor calls.
   * @default targetEnvironment
   */
  target?: VerifyTwoFactorProps['target'];

  /** Fixtures VerifyTwoFactor serves when `target` is `mock` (Storybook, API_ENV=mock). */
  mockConfig?: VerifyTwoFactorProps['mockConfig'];
};

// The mocked backend (API_ENV=mock) has no Janus, so VerifyTwoFactor serves its
// own TOTP challenge fixtures and accepts any code, as in Storybook.
const MOCK_TARGET: VerifyTwoFactorProps['target'] = 'mock';
const MOCK_CONFIG: VerifyTwoFactorProps['mockConfig'] = {
  enabledTypes: [[TwoFactorType.TOTP]],
  codeLength: 6,
};

export const StepUpTwoFactor = memo(function StepUpTwoFactor({
  onSuccess,
  onCancel,
  action = WORKSPACE_MEMBER_MANAGEMENT_ACTION,
  onError,
  onToast,
  loader,
  rootBoxProps,
  target = shouldMockdata ? MOCK_TARGET : targetEnvironment,
  mockConfig = shouldMockdata ? MOCK_CONFIG : undefined,
}: StepUpTwoFactorProps) {
  const { formatMessage } = useIntl();

  const handleBoundaryError = useCallback((boundaryError: TwoFactorBoundaryError) => {
    logError(boundaryError.error, {
      context: 'StepUpTwoFactor',
      twoFactorContext: boundaryError.context,
      componentStack: boundaryError.errorInfo?.componentStack,
      action: boundaryError.details.action,
      version: boundaryError.details.version,
    });
  }, []);

  // VerifyTwoFactor's own boundary mounts inside the lazy chunk, so a rejected
  // import throws past it to the app boundary and takes the whole route down.
  const handleChunkError = useCallback((error: Error, errorInfo: ErrorInfo) => {
    logError(error, {
      context: 'StepUpTwoFactor.chunk',
      componentStack: errorInfo.componentStack,
    });
  }, []);

  return (
    <ErrorBoundary
      fallback={
        <InAppErrorMessageFallback
          title={formatMessage(messages.loadErrorTitle)}
          description={formatMessage(messages.loadErrorDescription)}
        />
      }
      onError={handleChunkError}
    >
      <Suspense fallback={loader ?? null}>
        <VerifyTwoFactor
          target={target}
          mockConfig={mockConfig}
          clientId={oauthClientId}
          projectId={projectId}
          action={action}
          constraints={STEP_UP_CONSTRAINTS}
          onSuccess={onSuccess}
          onCancel={onCancel}
          onError={onError}
          onToast={onToast}
          onBoundaryError={handleBoundaryError}
          loader={loader}
          rootBoxProps={rootBoxProps}
        />
      </Suspense>
    </ErrorBoundary>
  );
});
